import {
    Box,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { ReactElement } from 'react';
import '../reports/Report.css';
import { CompanyDetailsDto } from '../../api/ClientApi';
import { DocCompType } from '../../redux/reducers/companyDetailsReducer';
import { generateWaybillNumber } from '../../common/Extensions';
import { FormatDateText } from '../../common/datesHelper';
import { translateClientField } from './CompanyDetailsHelper';

import { ResponsiveDialog } from '../../common/components/mobile/MobilePrimitives';

const makeSignatureElement = (title: string): ReactElement => {
    return (
        <>
            <span
                style={{
                    width: '100%',
                    height: 0,
                    display: 'inline-block',
                    borderBottom: '1px solid black',
                }}
            ></span>

            <Typography variant="caption" component="p" style={{ marginTop: '5px', textAlign: 'center' }}>
                {title}
            </Typography>
        </>
    );
};

const makeSignatureStampElement = (): ReactElement => {
    return (
        <div style={{ width: '100%' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <div style={{ width: '33%' }}>{makeSignatureElement('Robu izdao')}</div>
                <div style={{ width: '33%' }}>{makeSignatureElement('Robu preuzeo')}</div>
            </div>
        </div>
    );
};

type DocumentSellingTemplatePreviewProps = {
    companyDetails: CompanyDetailsDto | undefined;
    documentCompanyDetails: DocCompType[] | undefined;
    documentReceiverTemplate: DocCompType[] | undefined;
    open: boolean;
    closeForm(): void;
};

export default function DocumentSellingTemplatePreview(props: DocumentSellingTemplatePreviewProps): ReactElement {
    const { companyDetails, documentCompanyDetails, documentReceiverTemplate, open, closeForm } = props;

    return (
        <ResponsiveDialog maxWidth="xl" open={open} onClose={closeForm} disableEscapeKeyDown>
            <DialogContent
                dividers
                sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 }, width: { xs: '100%', sm: 600 }, height: '80vh' }}
            >
                <div>
                    <div className="waybill-report-content first-page-content" style={{ padding: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 50 }}>
                            <div
                                style={{
                                    width: '100%',
                                    margin: 0,
                                }}
                            >
                                <Typography variant="h6" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                                    Izdavalac
                                </Typography>
                                <hr />
                                {companyDetails?.name && (
                                    <Typography
                                        key={companyDetails?.name}
                                        variant="h6"
                                        style={{ textAlign: 'start', margin: '0px', color: 'black' }}
                                    >
                                        {companyDetails?.name}
                                    </Typography>
                                )}
                                {documentCompanyDetails?.map((dcd) => {
                                    return (
                                        <Typography
                                            key={dcd.index + dcd.label + dcd.value}
                                            variant="body2"
                                            style={{ textAlign: 'start', margin: '0px', color: 'black' }}
                                        >
                                            {(dcd.label ?? '') + ': ' + (dcd.value ?? '')}
                                        </Typography>
                                    );
                                })}
                            </div>
                            <div
                                style={{
                                    width: '100%',
                                    margin: 0,
                                }}
                            >
                                <Typography variant="h6" style={{ textAlign: 'end', margin: '0px', color: 'black' }}>
                                    Primalac
                                </Typography>
                                <hr />
                                {documentReceiverTemplate?.map((drt) => {
                                    return (
                                        <Typography
                                            key={drt.index + drt.label + drt.value}
                                            variant="body2"
                                            style={{ textAlign: 'end', margin: '0px', color: 'black' }}
                                        >
                                            {(drt.label ?? '') + ': ' + translateClientField(drt.value ?? '')}
                                        </Typography>
                                    );
                                })}
                            </div>
                        </div>
                        <Box style={{ margin: 25 }} />

                        <div>
                            <Typography variant="body2" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                                Broj: {generateWaybillNumber()}
                            </Typography>
                        </div>

                        <div>
                            <Typography variant="body2" style={{ textAlign: 'start', margin: '0px', color: 'black' }}>
                                Datum: {FormatDateText(new Date())}
                            </Typography>
                        </div>

                        <Typography
                            variant="h6"
                            style={{
                                textAlign: 'center',
                                margin: '0px',
                                color: 'black',
                                marginTop: 15,
                                marginBottom: 15,
                            }}
                        >
                            OTPREMNICA
                        </Typography>
                        <TableContainer sx={{ margin: 'auto' }}>
                            <Table size="small" stickyHeader aria-label="simple table">
                                <TableBody>
                                    <TableRow>
                                        <TableCell align="left">Vozilo:</TableCell>
                                        <TableCell align="left">Scania</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell align="left">Vozač:</TableCell>
                                        <TableCell align="left">Petar Petrović</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell align="left">JMBG:</TableCell>
                                        <TableCell align="left">1234567890123</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell align="left">Iz magacina:</TableCell>
                                        <TableCell align="left">Beograd</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell align="left">Na lokaciju:</TableCell>
                                        <TableCell align="left">Novi Sad</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography
                            variant="h6"
                            style={{
                                textAlign: 'center',
                                margin: '0px',
                                color: 'black',
                                marginBottom: 15,
                                marginTop: 15,
                            }}
                        >
                            Spisak robe
                        </Typography>

                        <TableContainer sx={{ margin: 'auto' }}>
                            <Table size="small" stickyHeader aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">Redni broj</TableCell>
                                        <TableCell align="center">Artikal</TableCell>
                                        <TableCell align="center">Količina</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell align="center">1</TableCell>
                                        <TableCell align="center">Artikal 1</TableCell>
                                        <TableCell align="center">30</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell align="center">2</TableCell>
                                        <TableCell align="center">Artikal 2</TableCell>
                                        <TableCell align="center">80</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell align="center">3</TableCell>
                                        <TableCell align="center">Artikal 3</TableCell>
                                        <TableCell align="center">50</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <div
                            style={{
                                width: '100%',
                                marginTop: 50,
                                paddingBottom: 50,
                            }}
                        >
                            {makeSignatureStampElement()}
                        </div>

                        <footer
                            style={{
                                position: 'absolute',
                                bottom: 20,
                                left: 0,
                                width: '100%',
                                textAlign: 'center',
                                fontSize: '20px',
                            }}
                        >
                            1/1
                        </footer>
                    </div>
                </div>
            </DialogContent>
        </ResponsiveDialog>
    );
}
